import React from 'react';
import { cn } from '../../lib/utils';
import { usePageContext, generateAutoTrackingId, pushGTMEvent } from '../../utils/tracking';

export interface InputProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  // Tracking props
  trackingId?: string; // Custom tracking ID override
  trackingSection?: string; // Section context for tracking
  disableTracking?: boolean; // Disable automatic tracking
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ 
    className, 
    type, 
    placeholder,
    onChange,
    onFocus,
    onBlur,
    trackingId,
    trackingSection,
    disableTracking = false,
    ...props 
  }, ref) => {
    const { pageName, section } = usePageContext();
    
    // Generate tracking ID if not provided and tracking is enabled
    const autoTrackingId = !disableTracking && !trackingId ? 
      generateAutoTrackingId({
        elementType: 'input',
        pageName,
        section: trackingSection || section,
        placeholder,
        ariaLabel: props['aria-label'],
        title: props.title
      }) : trackingId;

    // Enhanced event handlers with tracking
    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disableTracking && autoTrackingId) {
        pushGTMEvent({
          elementType: 'input',
          elementId: autoTrackingId,
          elementText: placeholder,
          pageName,
          section: trackingSection || section,
          variant: type,
          userAction: 'change'
        });
      }
      
      if (onChange) {
        onChange(event);
      }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (!disableTracking && autoTrackingId) {
        pushGTMEvent({
          elementType: 'input',
          elementId: autoTrackingId,
          elementText: placeholder,
          pageName,
          section: trackingSection || section,
          variant: type,
          userAction: 'focus'
        });
      }
      
      if (onFocus) {
        onFocus(event);
      }
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (!disableTracking && autoTrackingId) {
        pushGTMEvent({
          elementType: 'input',
          elementId: autoTrackingId,
          elementText: placeholder,
          pageName,
          section: trackingSection || section,
          variant: type,
          userAction: 'blur'
        });
      }
      
      if (onBlur) {
        onBlur(event);
      }
    };

    return (
      <input
        type={type}
        className={cn(
          'flex h-9 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1 text-sm text-gray-900 dark:text-gray-100 shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 dark:placeholder:text-gray-400 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-500 dark:focus-visible:ring-blue-400 disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        ref={ref}
        placeholder={placeholder}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        id={autoTrackingId}
        data-tracking-id={autoTrackingId}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

export { Input };